export const SERVICES = [
  {
    slug: "medicaid-pending-recovery",
    number: "01",
    title: "Medicaid Pending Recovery and Revenue Protection",
    short:
      "Convert stalled Medicaid pending accounts into paid days and stabilize facility cash flow.",
    problem:
      "Medicaid pending balances climb past 30, 60, and sometimes 90 days. Applications stall between departments while the facility absorbs the cost of care.",
    impact:
      "Six and seven figure accounts receivable exposure puts pressure on operating margins, payroll planning, and admissions decisions. The financial picture stops reflecting the care being delivered.",
    risk:
      "Charge offs at fiscal close, friction with corporate ownership, and reluctance to accept future skilled admits because pending exposure is already too high.",
    solution:
      "We rebuild the Medicaid pending process from intake through approval. That includes eligibility verification, documentation cadence, state specific submission protocols, named case ownership, and a weekly accountability rhythm that brings pending days down and keeps them down.",
  },
  {
    slug: "survey-readiness",
    number: "02",
    title: "Survey Readiness and Compliance Risk Reduction",
    short:
      "Prepare the facility, staff, and documentation for the next annual or complaint survey.",
    problem:
      "Survey teams arrive and find documentation gaps, inconsistent care planning, and interdisciplinary team communication breakdowns that leadership did not know existed.",
    impact:
      "F-tags, immediate jeopardy exposure, civil monetary penalties, denial of payment for new admissions, and erosion of referral relationships.",
    risk:
      "Plans of correction the facility cannot sustain past day 90, repeat deficiencies on the next survey, and ownership level scrutiny that follows across the portfolio.",
    solution:
      "We conduct mock surveys with the same rigor as a state entry, audit documentation against the State Operations Manual, run IDT readiness drills, and rebuild the records and routines that hold up under observation.",
  },
  {
    slug: "social-services-audit",
    number: "03",
    title: "Social Services Documentation Audits",
    short:
      "Close the documentation gaps that quietly create survey and liability exposure.",
    problem:
      "Social services notes are inconsistent. Grievance logs are incomplete. Psychosocial assessments lack the clinical depth surveyors look for.",
    impact:
      "Family grievances escalate, ombudsman involvement grows, and the paper trail does not match what is actually happening at the bedside.",
    risk:
      "Direct liability exposure when the record cannot defend the facility's actions, plus a department leader carrying clinical and regulatory weight without the systems to support it.",
    solution:
      "We audit every social services touchpoint, rebuild the documentation cadence, train staff on defensible note writing, and install department standards the facility can hold without burning out the team.",
  },
  {
    slug: "discharge-planning",
    number: "04",
    title: "Discharge Planning Workflow Review",
    short:
      "Build a discharge process that protects residents, revenue, and the facility's reputation.",
    problem:
      "Discharge planning happens reactively. Notice timing is inconsistent. Safe discharge documentation is thin. IDT decisions are not anchored in the record.",
    impact:
      "Avoidable readmissions, family disputes, unsafe discharge allegations, and lost length of stay opportunities that erode revenue per bed.",
    risk:
      "Civil exposure under federal and state safe discharge requirements, along with reputational damage that affects hospital partnerships and managed care relationships.",
    solution:
      "We rebuild the discharge workflow from admission day planning through 30 day post discharge follow up. Every step has audit ready documentation and a named owner.",
  },
  {
    slug: "operational-stabilization",
    number: "05",
    title: "Operational Stabilization Consulting",
    short:
      "Stabilize a facility under pressure before staff burnout becomes operational collapse.",
    problem:
      "Department heads run reactively. Communication is fractured. Systems exist on paper but not in practice. Decisions get made in hallways rather than meetings.",
    impact:
      "Turnover climbs, key positions cycle, family complaints accumulate, and the building loses the operational confidence that drives census and quality outcomes.",
    risk:
      "Census decline, regulatory scrutiny, escalating agency spend, and the slow erosion of facility identity that takes years to rebuild.",
    solution:
      "We perform on the ground operational diagnostics, coach leadership, restructure departmental workflows, and install meeting cadences and accountability rhythms that continue working long after the engagement ends.",
  },
  {
    slug: "litigation-support",
    number: "06",
    title: "Healthcare Litigation and Operational Support",
    short:
      "Translate facility operations into language attorneys, experts, and juries can defend.",
    problem:
      "Defense counsel needs operational context. They need to understand what the standard of care looks like in long-term care, how documentation flows, and where the facility's record actually tells the right story.",
    impact:
      "Cases settle higher than they should because the operational story is never told clearly. Or facilities are exposed because they cannot demonstrate the systems they actually had in place.",
    risk:
      "Adverse verdicts, inflated settlements, and operational changes driven by litigation rather than by strategy.",
    solution:
      "We provide consulting support for defense teams, including operational chronologies, record reconstruction, expert preparation collaboration, and strategic context from inside the building.",
  },
  {
    slug: "staff-education",
    number: "07",
    title: "Staff Education and Leadership Development",
    short:
      "Develop the administrators, directors, and department heads the facility actually needs.",
    problem:
      "New administrators inherit broken systems. Department heads are promoted without the training the role requires. Leadership development is improvised rather than structured.",
    impact:
      "Inconsistent execution, recurring compliance findings, and turnover in the exact positions the facility cannot afford to lose.",
    risk:
      "A leadership bench that cannot scale with the operator's portfolio, and survey outcomes that vary widely from building to building.",
    solution:
      "We design custom training programs for SNF leadership across compliance literacy, IDT facilitation, documentation defensibility, family communication, and survey response.",
  },
  {
    slug: "process-improvement",
    number: "08",
    title: "Facility Process Improvement Strategy",
    short:
      "Install the operational systems that produce predictable, high quality outcomes.",
    problem:
      "Workflows live in tribal knowledge. New hires take months to ramp up. Quality outcomes vary by who is on shift.",
    impact:
      "QAPI initiatives stall, performance improvement stays theoretical, and the building cannot scale beyond the energy of its strongest leaders.",
    risk:
      "Operational drift, slow QAPI maturity, and a facility that cannot demonstrate process discipline when surveyors, payers, or attorneys come looking.",
    solution:
      "We document the workflows, build departmental dashboards, install audit cadences, and create a process architecture the entire interdisciplinary team can execute against on a repeatable basis.",
  },
];

export const COMMON_PROBLEMS = [
  "Medicaid pending balances climbing past 30 days",
  "Accounts receivable rising with no clear workflow ownership",
  "Survey risk created by documentation gaps",
  "Social services compliance weaknesses",
  "Inconsistent discharge planning practices",
  "Interdisciplinary communication breakdowns",
  "Operational stress and instability across departments",
  "Family complaints and grievance exposure",
  "Workflow breakdowns no one person can fix alone",
];

export const WHO_WE_SERVE = [
  {
    title: "Skilled Nursing Facilities",
    desc: "Buildings facing operational, financial, or regulatory pressure.",
  },
  {
    title: "Long-Term Care Organizations",
    desc: "Multi facility operators working to standardize performance across a portfolio.",
  },
  {
    title: "Assisted Living Organizations",
    desc: "Communities navigating compliance, documentation, and resident acuity changes.",
  },
  {
    title: "Healthcare Startups",
    desc: "Early stage healthcare ventures building operational and compliance infrastructure.",
  },
  {
    title: "Healthcare Operators",
    desc: "Owners and executives stabilizing acquisitions or troubled assets.",
  },
  {
    title: "Healthcare Attorneys",
    desc: "Defense counsel who need operational fluency inside long-term care matters.",
  },
];
